import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, User, Mail, Phone, FileText, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';
import { useState, useEffect } from 'react';

import { Candidate, CandidateCreate } from '../../lib/types/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';

const candidateSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name is too long'),
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address (e.g., john.doe@company.com)'),
  phone: z.string().optional().refine((val) => {
    if (!val || val.trim() === '') return true; // Optional field
    // Basic phone validation - allows various formats
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(val.replace(/[\s\-\(\)]/g, ''));
  }, 'Please enter a valid phone number (e.g., +1-555-123-4567)'),
});

type CandidateFormData = z.infer<typeof candidateSchema>;

interface CandidateFormProps {
  candidate?: Candidate | null;
  onSubmit: (data: CandidateCreate) => void;
  onClose: () => void;
  isLoading?: boolean;
}

export function CandidateForm({ candidate, onSubmit, onClose, isLoading }: CandidateFormProps) {
  const [hasChanges, setHasChanges] = useState(false);
  const [changedFields, setChangedFields] = useState<string[]>([]);
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm<CandidateFormData>({
    resolver: zodResolver(candidateSchema),
    defaultValues: {
      name: candidate?.name || '',
      email: candidate?.email || '',
      phone: candidate?.phone || '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  // Watch form values to detect changes
  const formValues = watch();

  useEffect(() => {
    if (!candidate) return;

    const changed: string[] = [];
    if (formValues.name !== candidate.name) changed.push('Name');
    if (formValues.email !== candidate.email) changed.push('Email');
    if (formValues.phone !== candidate.phone) changed.push('Phone');

    setChangedFields(changed);
    setHasChanges(changed.length > 0);
  }, [formValues, candidate]);

  const handleFormSubmit = (data: CandidateFormData) => {
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl border border-glass-border/50 bg-glass-surface/95 backdrop-blur-xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                candidate 
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30' 
                  : 'bg-success-500/20 text-success-400 border border-success-500/30'
              }`}>
                <User className="w-5 h-5" />
              </div>
            <div>
              <h2 className="text-xl font-semibold text-text-enterprise-primary">
                {candidate ? 'Edit Candidate' : 'Add New Candidate'}
              </h2>
                <p className="text-sm text-text-enterprise-secondary mt-0.5">
                  {candidate ? 'Update contact information' : 'Enter candidate details'}
              </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-text-enterprise-tertiary hover:text-text-enterprise-primary hover:bg-glass-surface/50 rounded-lg"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Important Notice for Edit Mode */}
          {candidate && (
            <div className="mb-6 p-4 bg-primary-500/10 border border-primary-500/30 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <RefreshCw className="w-4 h-4 text-primary-400" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-primary-300 mb-1">
                    Synchronized Update
                  </div>
                  <p className="text-xs text-primary-400/80 leading-relaxed">
                    Changes to name, email, or phone will update <strong>all job submissions</strong> for this candidate, 
                    ensuring consistency across all JD vs CV analyses.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Changes Preview */}
          {candidate && hasChanges && (
            <div className="mb-6 p-4 bg-warning-500/10 border border-warning-500/30 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-4 h-4 text-warning-400 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-warning-300 mb-2">
                    Fields being updated:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {changedFields.map((field) => (
                      <span 
                        key={field}
                        className="px-2 py-1 bg-warning-500/20 border border-warning-500/30 rounded-md text-xs text-warning-300 font-medium"
                      >
                        {field}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            {/* Name Field */}
            <div className="space-y-2">
              <label htmlFor="name" className="block text-sm font-medium text-text-enterprise-primary">
                Full Name <span className="text-error-500">*</span>
              </label>
              <div className="relative group">
                <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors ${
                  errors.name 
                    ? 'text-error-500' 
                    : formValues.name !== candidate?.name && candidate
                    ? 'text-warning-400'
                    : 'text-text-enterprise-tertiary group-focus-within:text-primary-400'
                }`} />
                <Input
                  id="name"
                  type="text"
                  placeholder="e.g., John Doe"
                  className={`pl-10 transition-all ${
                    errors.name 
                      ? 'border-error-500 focus:border-error-500 focus:ring-error-500/20' 
                      : formValues.name !== candidate?.name && candidate
                      ? 'border-warning-500/50 focus:border-warning-500 focus:ring-warning-500/20'
                      : 'border-glass-border focus:border-primary-500 focus:ring-primary-500/20'
                  }`}
                  {...register('name')}
                />
                {formValues.name !== candidate?.name && candidate && !errors.name && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-2 h-2 bg-warning-500 rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>
              {errors.name && (
                <div className="mt-2 p-2 bg-error-500/10 border border-error-500/30 rounded-lg">
                  <p className="text-error-500 text-sm flex items-center gap-2">
                    <AlertCircle className="w-3 h-3" />
                    {errors.name.message}
                  </p>
                  <p className="text-text-enterprise-tertiary text-xs mt-1 ml-5">
                    Format: First Name Last Name (e.g., John Doe)
                  </p>
                </div>
              )}
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-text-enterprise-primary">
                Email Address <span className="text-error-500">*</span>
              </label>
              <div className="relative group">
                <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors ${
                  errors.email 
                    ? 'text-error-500' 
                    : formValues.email !== candidate?.email && candidate
                    ? 'text-warning-400'
                    : 'text-text-enterprise-tertiary group-focus-within:text-primary-400'
                }`} />
                <Input
                  id="email"
                  type="email"
                  placeholder="e.g., john.doe@company.com"
                  className={`pl-10 transition-all ${
                    errors.email 
                      ? 'border-error-500 focus:border-error-500 focus:ring-error-500/20' 
                      : formValues.email !== candidate?.email && candidate
                      ? 'border-warning-500/50 focus:border-warning-500 focus:ring-warning-500/20'
                      : 'border-glass-border focus:border-primary-500 focus:ring-primary-500/20'
                  }`}
                  {...register('email')}
                />
                {formValues.email !== candidate?.email && candidate && !errors.email && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-2 h-2 bg-warning-500 rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>
              {errors.email && (
                <div className="mt-2 p-2 bg-error-500/10 border border-error-500/30 rounded-lg">
                  <p className="text-error-500 text-sm flex items-center gap-2">
                    <AlertCircle className="w-3 h-3" />
                    {errors.email.message}
                  </p>
                  <p className="text-text-enterprise-tertiary text-xs mt-1 ml-5">
                    Format: username@domain.com
                  </p>
                </div>
              )}
            </div>

            {/* Phone Field */}
            <div className="space-y-2">
              <label htmlFor="phone" className="block text-sm font-medium text-text-enterprise-primary">
                Phone Number <span className="text-text-enterprise-tertiary text-xs">(Optional)</span>
              </label>
              <div className="relative group">
                <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors ${
                  errors.phone 
                    ? 'text-error-500' 
                    : formValues.phone !== candidate?.phone && candidate
                    ? 'text-warning-400'
                    : 'text-text-enterprise-tertiary group-focus-within:text-primary-400'
                }`} />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="e.g., +91-9876543210"
                  className={`pl-10 transition-all ${
                    errors.phone 
                      ? 'border-error-500 focus:border-error-500 focus:ring-error-500/20' 
                      : formValues.phone !== candidate?.phone && candidate
                      ? 'border-warning-500/50 focus:border-warning-500 focus:ring-warning-500/20'
                      : 'border-glass-border focus:border-primary-500 focus:ring-primary-500/20'
                  }`}
                  {...register('phone')}
                />
                {formValues.phone !== candidate?.phone && candidate && !errors.phone && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-2 h-2 bg-warning-500 rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>
              {errors.phone && (
                <div className="mt-2 p-2 bg-error-500/10 border border-error-500/30 rounded-lg">
                  <p className="text-error-500 text-sm flex items-center gap-2">
                    <AlertCircle className="w-3 h-3" />
                    {errors.phone.message}
                  </p>
                  <p className="text-text-enterprise-tertiary text-xs mt-1 ml-5">
                    Format: +country-code-number (e.g., +91-9876543210)
                  </p>
                </div>
              )}
              {!errors.phone && formValues.phone && (
                <p className="text-text-enterprise-tertiary text-xs ml-1">
                  ✓ Valid international format
                </p>
              )}
            </div>

            {/* CV Information */}
            {candidate?.cv_path && (
              <div className="bg-glass-primary/10 border border-glass-primary/30 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-primary-300">CV Available</span>
                    <p className="text-primary-400/80 text-xs mt-0.5">
                      Resume uploaded and processed
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Parsed Fields Display */}
            {candidate?.parsed_fields && Object.keys(candidate.parsed_fields).length > 0 && (
              <div className="bg-glass-success/10 border border-glass-success/30 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-success-500/20 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-success-400" />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-success-300">CV Processed</span>
                    <p className="text-success-400/80 text-xs mt-0.5">
                      Data extracted from resume
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 mt-3 pt-3 border-t border-success-500/20">
                  {candidate.parsed_fields.skills && (
                    <div className="flex items-start gap-2">
                      <span className="text-xs text-success-400/80 font-medium min-w-[70px]">Skills:</span>
                      <span className="text-xs text-success-300 flex-1">
                        {candidate.parsed_fields.skills.slice(0, 4).join(', ')}
                        {candidate.parsed_fields.skills.length > 4 && ` +${candidate.parsed_fields.skills.length - 4} more`}
                      </span>
                    </div>
                  )}
                  {candidate.parsed_fields.experience_years && (
                    <div className="flex items-start gap-2">
                      <span className="text-xs text-success-400/80 font-medium min-w-[70px]">Experience:</span>
                      <span className="text-xs text-success-300">
                        {candidate.parsed_fields.experience_years} years
                      </span>
                    </div>
                  )}
                  {candidate.parsed_fields.current_position && (
                    <div className="flex items-start gap-2">
                      <span className="text-xs text-success-400/80 font-medium min-w-[70px]">Position:</span>
                      <span className="text-xs text-success-300">
                        {candidate.parsed_fields.current_position}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex items-center gap-3 pt-6 border-t border-glass-border/30 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1 border-glass-border hover:bg-glass-surface/50"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className={`flex-1 relative overflow-hidden group ${
                  !isValid || isLoading
                    ? 'opacity-50 cursor-not-allowed'
                    : 'bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600'
                }`}
                disabled={!isValid || isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {candidate ? 'Updating...' : 'Creating...'}
                  </span>
                ) : (
                  <span className="relative z-10">
                    {candidate ? (
                      <>
                        {hasChanges ? '✓ Update Candidate' : 'No Changes'}
                      </>
                    ) : (
                      '+ Create Candidate'
                    )}
                  </span>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-secondary-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </Button>
            </div>
            
            {/* Validation Status */}
            {!isValid && Object.keys(errors).length > 0 && (
              <div className="mt-4 p-3 bg-error-500/10 border border-error-500/30 rounded-xl">
                <div className="flex items-center gap-2 text-error-400">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium">Please fix the errors above</span>
                </div>
                <p className="text-error-400/80 text-xs mt-1.5 ml-6">
                  All required fields (marked with *) must be filled correctly
                </p>
              </div>
            )}

            {/* No Changes Notice */}
            {candidate && !hasChanges && isValid && (
              <div className="mt-4 p-3 bg-glass-surface/30 border border-glass-border/30 rounded-xl">
                <div className="flex items-center gap-2 text-text-enterprise-secondary">
                  <CheckCircle2 className="w-4 h-4 text-success-400 flex-shrink-0" />
                  <span className="text-sm">No changes detected</span>
                </div>
              </div>
            )}
          </form>
        </div>
      </Card>
    </div>
  );
}
